import { Navigate, useParams, type RouteObject } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib'
import { PayoutsPage } from './PayoutsPage'
import { PartnersPage } from './PartnersPage'
import { BillsPage } from './BillsPage'
import { FixPage } from './FixPage'
import { TravelPage } from './TravelPage'
import { GoPage, ThingPage } from './ThingPage'
import { Busy } from './kit'
import { TodayPage } from './today/TodayPage'
import { CompaniesPage } from './records/CompaniesPage'
import { CompanyPage } from './records/CompanyPage'
import { PeoplePage } from './records/PeoplePage'
import { PersonPage } from './records/PersonPage'
import { PipelinePage } from './pipeline/PipelinePage'
import { SendPage } from './pipeline/SendPage'
import { FunnelPage } from './numbers/FunnelPage'
import { OutreachPage } from './numbers/OutreachPage'
import { CallsPage } from './numbers/CallsPage'
import { SystemPage } from './system/SystemPage'
import { JobsPage } from './system/JobsPage'
import { LogPage } from './system/LogPage'
import { ImportsPage } from './system/ImportsPage'

/** An old /staff/growth/prospect/<domain> link opens the company with that domain. */
function ProspectRedirect() {
  const { domain = '' } = useParams()
  const q = useQuery({
    queryKey: ['staff', 'by-domain', domain],
    queryFn: () => api<{ groups: { kind: string; rows: { path: string }[] }[] }>('GET', `/api/staff/search?q=${encodeURIComponent(domain)}`),
  })
  if (q.isLoading) return <Busy />
  const path = q.data?.groups.find((g) => g.kind === 'record')?.rows[0]?.path
  return <Navigate to={path ?? '/staff/companies'} replace />
}

/** An old /staff/growth/people/<id> link held a company or user id: /staff/go resolves either. */
function OldPerson() {
  const { id = '' } = useParams()
  return <Navigate to={`/staff/go/${id}`} replace />
}

// CHANGE-070: one app. Every /staff page lives here (the card and agent access features add theirs). Old URLs keep
// working, because links in email and Slack point at them.
export const routes: RouteObject[] = [
  { path: '/staff', element: <TodayPage /> },
  { path: '/staff/pipeline', element: <Navigate to="/staff/pipeline/partners" replace /> },
  { path: '/staff/pipeline/:track', element: <PipelinePage /> },
  { path: '/staff/send', element: <SendPage /> },
  { path: '/staff/calls', element: <CallsPage /> },
  { path: '/staff/companies', element: <CompaniesPage /> },
  { path: '/staff/companies/:id', element: <CompanyPage /> },
  { path: '/staff/people', element: <PeoplePage /> },
  { path: '/staff/people/:id', element: <PersonPage /> },
  { path: '/staff/funnel', element: <FunnelPage /> },
  { path: '/staff/outreach', element: <OutreachPage /> },
  { path: '/staff/system', element: <SystemPage /> },
  { path: '/staff/jobs', element: <JobsPage /> },
  { path: '/staff/imports', element: <ImportsPage /> },
  { path: '/staff/payouts', element: <PayoutsPage /> },
  { path: '/staff/partners', element: <PartnersPage /> },
  { path: '/staff/bills', element: <BillsPage /> },
  { path: '/staff/travel', element: <TravelPage /> },
  { path: '/staff/fix', element: <FixPage /> },
  { path: '/staff/log', element: <LogPage /> },
  { path: '/staff/cases', element: <Navigate to="/staff#cases" replace /> },
  { path: '/staff/growth', element: <Navigate to="/staff" replace /> },
  { path: '/staff/growth/people', element: <Navigate to="/staff/companies" replace /> },
  { path: '/staff/growth/people/:id', element: <OldPerson /> },
  { path: '/staff/growth/funnel', element: <Navigate to="/staff/funnel" replace /> },
  { path: '/staff/growth/calls', element: <Navigate to="/staff/calls" replace /> },
  { path: '/staff/growth/prospect/:domain', element: <ProspectRedirect /> },
  { path: '/staff/growth/:tab', element: <Navigate to="/staff/outreach" replace /> },
  { path: '/staff/go/:id', element: <GoPage /> },
  { path: '/staff/record/:table/:id', element: <ThingPage /> },
  { path: '/staff/:kind/:id', element: <ThingPage /> },
]
